import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/WEAVE/images";

export default function WEAVEPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const diagramFrames = [
    `${BASE}/renders/Diagrams/Image47.png`,
    `${BASE}/renders/Diagrams/Image47_000.png`,
    `${BASE}/renders/Diagrams/Image48.png`,
    `${BASE}/renders/Diagrams/Image49.png`,
    `${BASE}/renders/Diagrams/Image50.png`,
  ];

  const diagramCaps = [
    "Massing 01",
    "Massing 02",
    "Circulation",
    "Structure",
    "Envelope",
  ];

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="WEAVE"
          description="A speculative museum of extinction set on the shore of a lake, threading building, deck, and landscape into one continuous woven structure."
          meta={["Academic", "Lake shore", "Rhino", "Grasshopper", "2020"]}
        />

        {/* Hero — exterior render */}
        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/renders/Image34_000.png`}
                alt="WEAVE — exterior render across the lake"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        {/* Design & formation */}
        <BlockSection title="Design and formation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Aiming for comprehensive integration with nature, the project sits on the lake’s shore and extends a wooden deck across the water to the opposite bank. The building’s spaces and circulation are organised to enrich the visitor’s experience of the natural environment — easing accessibility and framing multiple viewpoints across the lake.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/exploded-removebg-preview.png`}
                  alt="WEAVE — exploded axonometric of the woven structural system"
                  ratio="classic"
                  fit="contain"
                  caption="Exploded structural system"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <ImageSequence
                  images={diagramFrames}
                  captions={diagramCaps}
                  label="Form diagrams"
                  ratio="classic"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Architectural system */}
        <BlockSection title="Architectural system">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The structural strategy weaves slender timber elements through a continuous shell, expressing the museum’s narrative — the fragile interlacing of species — in built form.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/Artboard 1.png`}
                  alt="WEAVE — architectural system board"
                  ratio="banner"
                  fit="contain"
                  invert
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Journey & planning */}
        <BlockSection title="Journey and planning">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Sectional studies trace the visitor’s path from shore to deck to building — calibrating thresholds, view cones, and grade changes along the route.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/Artboard Sections.png`}
                  alt="WEAVE — sectional studies of the visitor journey"
                  ratio="banner"
                  fit="contain"
                  invert
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Renderings */}
        <BlockSection title="Renderings and visualisations">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/renders/Image45.png`}
                  alt="WEAVE — render, view 1"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/renders/Image37.png`}
                  alt="WEAVE — render, view 2"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/renders/Image44_000.png`}
                  alt="WEAVE — render, view 3"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/renders/Image44.png`}
                  alt="WEAVE — render, view 4"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
